// ─── РОЛИ СОТРУДНИКОВ: ОДИН СЛОВАРЬ ДЛЯ API И КАБИНЕТА ───────────────────────
// 03.10 (владелец: «ОО нанимает и увольняет только операторов; ДК, ОО и ИО —
// только ИО»). Здесь роли staff.role / custom_perms.extra_roles, их порядок
// (основная роль = самая старшая), русские подписи и правило «кто кем управляет».
// Сервер решает по canManageRoles, интерфейс прячет кнопки по нему же.

export const STAFF_ROLES = ["eo", "oo", "dc", "operator", "admin_hall"] as const;
export type StaffRoleId = (typeof STAFF_ROLES)[number];

/** Старшинство: основная роль строки staff — самая старшая из набора. */
export const STAFF_ROLE_PRIORITY: Record<StaffRoleId, number> = {
  eo: 4, oo: 3, dc: 2, operator: 1, admin_hall: 1,
};

export const STAFF_ROLE_LABELS: Record<StaffRoleId, string> = {
  eo: "EO", oo: "ОО", dc: "ДК", operator: "Оператор", admin_hall: "Админ зала",
};

/** Кого нанимает и увольняет ОО (право staff_hire). Остальных — только ИО. */
export const OO_MANAGEABLE: readonly StaffRoleId[] = ["operator"];

/** Руководящие роли: такие строки правит только ИО (имя, телефон, Telegram, статус). */
export const MANAGER_ROLES: readonly StaffRoleId[] = ["eo", "oo"];

export function isStaffRole(x: unknown): x is StaffRoleId {
  return typeof x === "string" && (STAFF_ROLES as readonly string[]).includes(x);
}

/** Только известные роли, без повторов, от старшей к младшей. */
export function sortRoles(input: readonly unknown[]): StaffRoleId[] {
  const out: StaffRoleId[] = [];
  for (const r of input) if (isStaffRole(r) && !out.includes(r)) out.push(r);
  return out.sort((a, b) => STAFF_ROLE_PRIORITY[b] - STAFF_ROLE_PRIORITY[a]);
}

/** Роли строки staff: основная role + custom_perms.extra_roles (битый JSON — только основная). */
export function parseRoles(row: { role?: string | null; custom_perms?: string | null }): StaffRoleId[] {
  const all: unknown[] = [row.role];
  try {
    const cp = JSON.parse(row.custom_perms || "{}") as { extra_roles?: unknown };
    if (Array.isArray(cp.extra_roles)) all.push(...cp.extra_roles);
  } catch { /* битый custom_perms — только основная роль */ }
  return sortRoles(all);
}

/** Основная роль в staff.role, остальные — в custom_perms.extra_roles. */
export function splitRoles(roles: readonly StaffRoleId[]): { primary: StaffRoleId; extra: StaffRoleId[] } | null {
  const sorted = sortRoles(roles);
  return sorted.length ? { primary: sorted[0]!, extra: sorted.slice(1) } : null;
}

/**
 * Может ли человек с ролями actorRoles нанять / уволить / вернуть того, у кого targetRoles.
 * ИО — кого угодно; ОО — только если ВСЕ роли цели из OO_MANAGEABLE; остальные — никого.
 * Пустой набор ролей цели — нельзя (неизвестно, кого трогаем).
 */
export function canManageRoles(actorRoles: readonly string[], targetRoles: readonly string[]): boolean {
  if (actorRoles.includes("eo")) return true;
  if (!actorRoles.includes("oo") || targetRoles.length === 0) return false;
  return targetRoles.every((r) => (OO_MANAGEABLE as readonly string[]).includes(r));
}

/** Есть ли у цели руководящая роль (eo/oo) — такую строку правит только ИО. */
export function hasManagerRole(roles: readonly string[]): boolean {
  return roles.some((r) => (MANAGER_ROLES as readonly string[]).includes(r));
}
